import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import styled from "styled-components";
import {ReactUtils} from "../../utils/ReactUtils";
import {fontSize, fontWeight, radius, zIndex} from "../styles/styles";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {useAppStore} from "../../../StateManager";
import {colorPalette} from "../styles/colorPalette";
import {DomUtils, HorizontalAlignment, VerticalAlignment, type TransformObj} from "../../../utils/dom/DomUtils";
import {MathUtils} from "../../../utils/math/MathUtils";

interface IInfoBubbleV5Props {
	readonly content: string | React.ReactNode;
	readonly isErrorMessage?: boolean;
	readonly divRef?: React.RefObject<HTMLDivElement>;
	readonly style?: React.CSSProperties;
	readonly className?: string;
	readonly title?: string;
	readonly hide?: boolean;
	readonly useDomPortal?: boolean;
	readonly anchorRef?: React.RefObject<Element>;
	readonly anchorElement?: Element;
	readonly verticalAlignment?: VerticalAlignment;
	readonly horizontalAlignment?: HorizontalAlignment;
	readonly positionOffsetY?: number;
	readonly positionOffsetX?: number;
	readonly changeAlignmentOnPositionCorrection?: boolean;
}

type InfoBubblePosition = {
	transform: TransformObj;
	arrowLeft: number;
};

const INFO_BUBBLE_ARROW_SIZE = 10;
const INFO_BUBBLE_ARROW_HALF_SIZE = INFO_BUBBLE_ARROW_SIZE / 2;
const INFO_BUBBLE_POSITION_TOLERANCE = 0.5;

const isPositionCloseEnough = (previousPosition: InfoBubblePosition, nextPosition: InfoBubblePosition) => {
	if (!previousPosition || !nextPosition) {
		return false;
	}

	return (
		previousPosition.transform.vertical === nextPosition.transform.vertical &&
		previousPosition.transform.horizontal === nextPosition.transform.horizontal &&
		Math.abs(previousPosition.transform.x - nextPosition.transform.x) <= INFO_BUBBLE_POSITION_TOLERANCE &&
		Math.abs(previousPosition.transform.y - nextPosition.transform.y) <= INFO_BUBBLE_POSITION_TOLERANCE &&
		Math.abs(previousPosition.arrowLeft - nextPosition.arrowLeft) <= INFO_BUBBLE_POSITION_TOLERANCE
	);
};

export const InfoBubbleV5 = (props: IInfoBubbleV5Props) => {
	const appState = useAppStore((state) => state.appState);
	const {
		isErrorMessage,
		content,
		divRef,
		style = {},
		title,
		className = "",
		hide = false,
		useDomPortal = true,
		anchorRef,
		anchorElement,
		verticalAlignment = VerticalAlignment.topOuter,
		horizontalAlignment = HorizontalAlignment.center,
		positionOffsetY = 5,
		positionOffsetX = 0,
		changeAlignmentOnPositionCorrection = true,
	} = props;
	const floatingElementRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<InfoBubblePosition>(null);
	const effectiveFloatingElementRef = divRef || floatingElementRef;

	// eslint-disable-next-line react-hooks/preserve-manual-memoization -- ref.current is intentionally read at callback execution time for tooltip positioning.
	const updatePosition = useCallback(() => {
		const anchor = anchorRef?.current ?? anchorElement;
		const floatingElement = effectiveFloatingElementRef.current;

		if (!anchor || !floatingElement || hide) {
			return;
		}

		const transform = DomUtils.getFixedFloatingElementPosition(
			anchor,
			floatingElement,
			verticalAlignment,
			horizontalAlignment,
			positionOffsetY,
			positionOffsetX,
			changeAlignmentOnPositionCorrection,
		);

		if (!transform) {
			return;
		}

		const anchorRect = anchor.getBoundingClientRect();
		const floating = floatingElement.getBoundingClientRect();
		const floatingStyle = getComputedStyle(floatingElement);
		const borderLeft = parseFloat(floatingStyle.borderLeftWidth) || 0;
		const borderRight = parseFloat(floatingStyle.borderRightWidth) || 0;
		const floatingPaddingBoxWidth = floating.width - borderLeft - borderRight;
		const minArrowLeft = INFO_BUBBLE_ARROW_HALF_SIZE;
		const maxArrowLeft = Math.max(minArrowLeft, floatingPaddingBoxWidth - INFO_BUBBLE_ARROW_HALF_SIZE);
		const unclampedArrowLeft = anchorRect.left + anchorRect.width / 2 - transform.x - borderLeft;
		const middleArrowOffsetX = unclampedArrowLeft > minArrowLeft && unclampedArrowLeft < maxArrowLeft ? 1 : 0;
		const arrowLeft = Math.min(Math.max(unclampedArrowLeft + middleArrowOffsetX, minArrowLeft), maxArrowLeft);

		if (!MathUtils.isValidNumber(transform.x) || !MathUtils.isValidNumber(transform.y) || !MathUtils.isValidNumber(arrowLeft)) {
			return;
		}

		const nextPosition = {transform, arrowLeft};

		setPosition((previousPosition) => (isPositionCloseEnough(previousPosition, nextPosition) ? previousPosition : nextPosition));
	}, [
		anchorRef,
		anchorElement,
		changeAlignmentOnPositionCorrection,
		effectiveFloatingElementRef,
		hide,
		horizontalAlignment,
		positionOffsetX,
		positionOffsetY,
		verticalAlignment,
	]);

	useLayoutEffect(() => {
		updatePosition();
	}, [updatePosition]);

	useEffect(() => {
		if ((!anchorRef && !anchorElement) || hide) {
			return;
		}

		let animationFrameId: number;

		const updateOnAnimationFrame = () => {
			updatePosition();
			animationFrameId = window.requestAnimationFrame(updateOnAnimationFrame);
		};

		animationFrameId = window.requestAnimationFrame(updateOnAnimationFrame);

		return () => window.cancelAnimationFrame(animationFrameId);
	}, [anchorRef, anchorElement, hide, updatePosition]);

	const positionedStyle =
		anchorRef || anchorElement
			? ({
					...style,
					position: "fixed",
					top: 0,
					left: 0,
					transform: position?.transform.translate,
					visibility: position ? style.visibility : "hidden",
					"--info-bubble-arrow-left": position ? `${position.arrowLeft}px` : undefined,
				} as React.CSSProperties)
			: style;

	const positionClassName = position
		? ReactUtils.cls({
				[VerticalAlignment[position.transform.vertical]]: true,
				"arrow-positioned": true,
			})
		: "";

	const renderBubble = () => {
		return (
			<InfoBubbleStyled
				ref={effectiveFloatingElementRef}
				style={positionedStyle}
				className={ReactUtils.cls(`info-bubble ${className || ""} ${positionClassName}`, {error: isErrorMessage})}
				$hide={hide}
			>
				<div className="content vbox">
					{!!title?.length && <h4>{title}</h4>}
					{content}
				</div>
			</InfoBubbleStyled>
		);
	};

	if (useDomPortal) {
		return <DomPortal destination={appState.app.modalContainer}>{renderBubble()}</DomPortal>;
	}

	return renderBubble();
};

export const InfoBubbleStyled = styled.div<{$hide?: boolean}>`
	z-index: ${zIndex.notification};
	background: ${colorPalette.blueGray.c900};
	box-shadow: 0px 4px 20px 0px #00000033;
	border-radius: ${radius.sm};
	font-size: ${fontSize.sm};
	line-height: 140%;
	position: absolute;
	width: max-content;
	max-width: 300px;
	color: ${colorPalette.white};
	border-left: none;
	white-space: initial;
	display: ${({$hide}) => ($hide ? "none" : "block")};

	&.error {
		border-color: ${colorPalette.negative.c500Primary};
		color: ${colorPalette.negative.c500Primary};
		background: ${colorPalette.white};
		border-left: ${colorPalette.negative.c500Primary} 4px solid;

		&::after {
			background: ${colorPalette.white};
		}
	}

	&.onPopup {
		z-index: ${zIndex.popup + zIndex.contextOptions};
	}

	&::after {
		content: "";
		background: ${colorPalette.blueGray.c900};
		transform: translateX(-50%) rotate(45deg);
		position: absolute;
		bottom: -5px;
		width: ${INFO_BUBBLE_ARROW_SIZE}px;
		height: ${INFO_BUBBLE_ARROW_SIZE}px;
		left: 50%;
	}

	&.left::after,
	&.arrow-right::after {
		left: auto;
		right: 10px;
		transform: translateX(0) rotate(45deg);
	}

	&.right::after,
	&.arrow-left::after {
		left: 10px;
		transform: translateX(0) rotate(45deg);
	}

	&.arrow-positioned::after {
		left: var(--info-bubble-arrow-left, 50%);
		right: auto;
		transform: translateX(-50%) rotate(45deg);
	}

	&.bottom::after,
	&.bottomOuter::after {
		top: -5px;
		bottom: auto;
	}

	div {
		word-wrap: break-word;
		padding: 10px;

		h4 {
			font-size: ${fontSize.sm};
			font-weight: ${fontWeight.bold};
			margin-bottom: 10px;
		}
	}
`;
